import type { SegmentsBoolExp } from "src/graphql";
import { getLabelFilterKeyAndValue } from "src/components/folders/filter-helpers";
import { simplifyHasuraFilters } from "src/utils/hasura";

export interface AudienceFilterParams {
  audienceTypes?: string[];
  parentIds?: string[];
  sourceIds?: string[];
  createdByIds?: string[];
  recentlyCreated?: string;
  labels?: string[];
  duplicateKeys?: string;
  subsetIds?: string[];
  inactiveSyncs?: string;
  unhealthySyncs?: string;
  recentlySynced?: string;
  matchboosterEnabled?: string;
  folderIds?: string[];
  search?: string;
  /**
   * When true, the builder omits the `is_realtime_audience: { _eq: false }`
   * predicate so realtime audiences appear in the list. Defaults to `false`
   * (hide realtime audiences), matching the pre-`enableRealtimeAudiences`-flag
   * behavior.
   */
  enableRealtimeAudiences?: boolean;
}

const inactiveSyncsBoolExp: SegmentsBoolExp = {
  syncs_aggregate: {
    count: {
      predicate: { _eq: 0 },
      filter: {
        _and: [
          { schedule_paused: { _neq: true } },
          { last_run_at: { _is_null: false } },
        ],
      },
    },
  },
};

function singleSelectedId(values: string[] | undefined): string | undefined {
  if (!values || values.length !== 1) return undefined;
  return values[0];
}

function buildAudienceTypeFilter(
  values: string[] | undefined,
): SegmentsBoolExp {
  const id = singleSelectedId(values);
  if (id === "realtime") return { is_realtime_audience: { _eq: true } };
  if (id === "standard") return { is_realtime_audience: { _eq: false } };
  return {};
}

function buildMatchboosterFilter(value: string | undefined): SegmentsBoolExp {
  if (value === "true") {
    return { parent: { matchboosting_enabled: { _eq: true } } };
  }
  if (value === "false") {
    return { parent: { matchboosting_enabled: { _eq: false } } };
  }
  return {};
}

function buildInactiveSyncsFilter(value: string | undefined): SegmentsBoolExp {
  if (value === "true") return inactiveSyncsBoolExp;
  if (value === "false") return { _not: inactiveSyncsBoolExp };
  return {};
}

function buildUnhealthySyncsFilter(value: string | undefined): SegmentsBoolExp {
  if (value === "true") return { syncs: { health: { _eq: "unhealthy" } } };
  if (value === "false") {
    return { _not: { syncs: { health: { _eq: "unhealthy" } } } };
  }
  return {};
}

function relativeTimestamp(value: string | undefined): string | undefined {
  if (!value) return undefined;
  // Truncate to the current minute so the loader prefetch and the component's
  // mount-time call produce the same timestamp within a minute-aligned window.
  // Without this, each call generates a fresh millisecond-precise ISO string
  // → different react-query cache keys → guaranteed prefetch miss whenever
  // recently_created or recently_synced is active.
  const now = new Date();
  now.setSeconds(0, 0);
  if (value === "30d") {
    now.setDate(now.getDate() - 30);
  } else if (value === "7d") {
    now.setDate(now.getDate() - 7);
  } else if (value === "24h") {
    now.setHours(now.getHours() - 24);
  } else {
    return undefined;
  }
  return now.toISOString();
}

function buildRecentlySyncedFilter(value: string | undefined): SegmentsBoolExp {
  const timestamp = relativeTimestamp(value);
  if (!timestamp) return {};
  return { syncs: { last_run_at: { _gte: timestamp } } };
}

function buildRecentlyCreatedFilter(
  value: string | undefined,
): SegmentsBoolExp {
  const timestamp = relativeTimestamp(value);
  if (!timestamp) return {};
  return { created_at: { _gte: timestamp } };
}

function buildSubsetsFilter(ids: string[] | undefined): SegmentsBoolExp {
  if (!ids?.length) return {};
  return { subsets: { subset_value_id: { _in: ids } } };
}

function buildParentFilter(ids: string[] | undefined): SegmentsBoolExp {
  if (!ids?.length) return {};
  return { visual_query_parent_id: { _in: ids } };
}

function buildSourceFilter(ids: string[] | undefined): SegmentsBoolExp {
  if (!ids?.length) return {};
  return { connection_id: { _in: ids } };
}

function buildDuplicateKeyFilter(value: string | undefined): SegmentsBoolExp {
  if (value === "true") {
    return {
      last_run_duplicate_primary_keys: { _gt: 0 },
      destination_instances: {
        last_run_planner_type: { _neq: "all", _is_null: false },
      },
    };
  }
  if (value === "false") {
    return {
      _and: [
        {
          _or: [
            { last_run_duplicate_primary_keys: { _eq: 0 } },
            { last_run_duplicate_primary_keys: { _is_null: true } },
            {
              _not: {
                destination_instances: {
                  last_run_planner_type: { _neq: "all", _is_null: false },
                },
              },
            },
          ],
        },
      ],
    };
  }
  return {};
}

function buildCreatedFilter(ids: string[] | undefined): SegmentsBoolExp {
  if (!ids?.length) return {};
  return {
    _or: [{ created_by: { _in: ids } }, { created_by: { _is_null: true } }],
  };
}

function buildLabelFilter(labels: string[] | undefined): SegmentsBoolExp {
  if (!labels?.length) return {};
  return {
    _or: labels.map((id) => {
      if (id === "-1") {
        return { tags: { _eq: {} } };
      }
      const { key, value } = getLabelFilterKeyAndValue(id);
      return { tags: { _contains: { [key]: value } } };
    }),
  };
}

function buildFolderFilter(ids: string[] | undefined): SegmentsBoolExp {
  if (!ids?.length) return {};
  return { folder_id: { _in: ids } };
}

function buildSearchFilter(search: string | undefined): SegmentsBoolExp {
  const value = search?.trim();
  if (!value) return {};
  // Valid audience ID is numeric, up to 19 digits (bigint).
  const isValidAudienceId = /^[1-9]\d{0,18}$/.test(value);
  if (isValidAudienceId) {
    return {
      _or: [{ name: { _ilike: `%${value}%` } }, { id: { _eq: value } }],
    };
  }
  return { name: { _ilike: `%${value}%` } };
}

/**
 * Builds the parent-and wrapper used by the component so that `parent` and
 * `source` filters compose as a single `_and` clause (matching pre-extraction
 * behavior at audiences.tsx).
 */
function buildParentAndSourceFilter(
  params: AudienceFilterParams,
): SegmentsBoolExp {
  const combined = {
    ...buildParentFilter(params.parentIds),
    ...buildSourceFilter(params.sourceIds),
  };
  if (Object.keys(combined).length === 0) return {};
  return { _and: [combined] };
}

/**
 * Build the full audiences-list `SegmentsBoolExp`, including the invariant
 * predicates that scope the list to visual audiences and (by default) hide
 * realtime audiences.
 *
 * Excludes the demo (IKEA KPI) filter — it requires server-loaded labels data
 * and is flag-gated — so the component composes it on top of this result.
 */
export function buildAudienceHasuraFilters(
  params: AudienceFilterParams,
): SegmentsBoolExp {
  return simplifyHasuraFilters({
    ...buildAudienceTypeFilter(params.audienceTypes),
    ...buildSubsetsFilter(params.subsetIds),
    ...buildParentAndSourceFilter(params),
    ...buildFolderFilter(params.folderIds),
    ...buildDuplicateKeyFilter(params.duplicateKeys),
    ...buildLabelFilter(params.labels),
    ...buildCreatedFilter(params.createdByIds),
    ...buildRecentlyCreatedFilter(params.recentlyCreated),
    ...buildMatchboosterFilter(params.matchboosterEnabled),
    ...buildInactiveSyncsFilter(params.inactiveSyncs),
    ...buildUnhealthySyncsFilter(params.unhealthySyncs),
    ...buildRecentlySyncedFilter(params.recentlySynced),
    ...buildSearchFilter(params.search),
    query_type: { _eq: "visual" },
    _not: { campaigns: {} },
    ...(params.enableRealtimeAudiences
      ? {}
      : { is_realtime_audience: { _eq: false } }),
  });
}

/**
 * Parse URL search params into `AudienceFilterParams`. Only reads params that
 * can be resolved without server data — folder descendants aren't expanded
 * here (the component resolves them via `useFolderState`).
 */
export function parseAudienceFilterParams(
  searchParams: URLSearchParams,
): AudienceFilterParams {
  const split = (key: string) => {
    const value = searchParams.get(key);
    return value ? value.split(",") : undefined;
  };
  const folderId = searchParams.get("folder");
  return {
    audienceTypes: split("audience_type"),
    parentIds: split("parent"),
    sourceIds: split("source"),
    createdByIds: split("created"),
    recentlyCreated: searchParams.get("recently_created") ?? undefined,
    labels: split("label"),
    duplicateKeys: searchParams.get("duplicate_keys") ?? undefined,
    subsetIds: split("subsets"),
    inactiveSyncs: searchParams.get("inactive_syncs") ?? undefined,
    unhealthySyncs: searchParams.get("unhealthy_syncs") ?? undefined,
    recentlySynced: searchParams.get("recently_synced") ?? undefined,
    matchboosterEnabled: searchParams.get("matchbooster_enabled") ?? undefined,
    folderIds: folderId ? [folderId] : undefined,
    search: searchParams.get("search") ?? undefined,
  };
}
